import { z } from 'zod'
import { dedupeEmailsCI } from './email'

/**
 * FO-111 + FO-112 (batch-22) — gedeelde schemas + DTO's voor de werkbon-
 * verzendflow: ontvanger/cc kiezen (FO-112) en de instelbare begeleidende
 * mailtekst (FO-111). Eén bron: zowel het verstuur-dialoog (client-side
 * validatie + inline veldfouten) als de API (`POST /werkbonnen/:id/verstuur`)
 * gebruiken deze schemas, zodat het formulier niets kan sturen dat de API
 * weigert (read↔write-consistentie, CLAUDE.md "Validatie & foutmeldingen").
 */

/**
 * Eén of meer geldige e-mailadressen, getrimd + case-insensitief ontdubbeld.
 * Gedeeld door `to`/`cc` op het verstuur-schema en de standaard-cc op de relatie.
 */
const emailListSchema = z
  .array(z.string().trim().email())
  .transform((arr) => dedupeEmailsCI(arr))

/**
 * Begeleidende mailtekst (onderwerp + body). Hergebruikt voor (a) de eenmalige
 * edit in het verstuur-dialoog en (b) de per-klant override op de relatie. Beide
 * velden verplicht niet-leeg wanneer aanwezig: een lege override slaan we niet op
 * (dan verwijder je 'm = terugval op de standaard), dus de read geeft nooit een
 * lege string terug die het write-schema zou weigeren.
 */
export const WerkbonMailTekstSchema = z.object({
  subject: z.string().trim().min(1, 'Onderwerp is verplicht').max(500),
  bodyHtml: z.string().trim().min(1, 'Tekst is verplicht'),
})
export type WerkbonMailTekstInput = z.infer<typeof WerkbonMailTekstSchema>

/**
 * Body van `POST /werkbonnen/:id/verstuur`. Alle velden optioneel: zonder `to`
 * valt de route terug op de ontvanger-resolver (cascade), zonder `eenmalig` op
 * de per-klant/standaard-tekst. `to`/`cc` worden server-side (opnieuw) met dit
 * schema gevalideerd en doorlopen daarna de bestaande mail-allowlist.
 */
export const VerstuurWerkbonSchema = z.object({
  to: emailListSchema.optional(),
  cc: emailListSchema.optional(),
  eenmalig: WerkbonMailTekstSchema.optional(),
})
export type VerstuurWerkbonInput = z.infer<typeof VerstuurWerkbonSchema>

/** Body van `POST /werkbonnen/:id/verstuur/voorbeeld` (live preview in het dialoog). */
export const WerkbonVerstuurVoorbeeldSchema = z.object({
  eenmalig: WerkbonMailTekstSchema.optional(),
})
export type WerkbonVerstuurVoorbeeldInput = z.infer<typeof WerkbonVerstuurVoorbeeldSchema>

/**
 * Vaste werkbon-ontvanger + standaard-cc per klant (FO-112), instelbaar op de
 * relatie-detail. `werkbonOntvangerContactId` is bij voorkeur een verwijzing naar
 * een contactpersoon (één bron, adres volgt de contactpersoon); een vrij adres
 * (`werkbonOntvangerEmail`) blijft mogelijk voor klanten zonder contactrecord.
 * `null` wist de waarde (terugval op de FO-109-contacten / `Client.email`).
 */
export const VasteWerkbonOntvangerSchema = z.object({
  werkbonOntvangerContactId: z.string().trim().min(1).nullable(),
  werkbonOntvangerEmail: z.string().trim().email().nullable(),
  werkbonStandaardCc: emailListSchema,
})
export type VasteWerkbonOntvangerInput = z.infer<typeof VasteWerkbonOntvangerSchema>

/** Bron van de effectieve mailtekst (resolutie-volgorde, hoog → laag). */
export type WerkbonMailBron = 'EENMALIG' | 'KLANT' | 'STANDAARD'

export const WERKBON_MAIL_BRON_LABELS: Record<WerkbonMailBron, string> = {
  EENMALIG: 'Eenmalig aangepast',
  KLANT: 'Eigen tekst voor deze klant',
  STANDAARD: 'Standaardtekst',
}

/** Contactpersoon-keuze voor de naar/cc-picker (alleen contacten mét e-mailadres). */
export interface WerkbonVerstuurContactDTO {
  id: string
  naam: string
  functie: string | null
  email: string
}

/**
 * Effectieve, server-side gerenderde mailtekst. De `bodyHtml` is de volledige
 * preview-HTML (placeholders ingevuld) zodat het dialoog exact toont wat de klant
 * krijgt ("preview = wat verstuurd wordt").
 */
export interface WerkbonMailEffectiefDTO {
  bron: WerkbonMailBron
  /** Onderwerp met placeholders ingevuld. */
  subject: string
  /** Volledige, gerenderde HTML-body voor de preview. */
  bodyHtml: string
}

/** Alles wat het verstuur-dialoog nodig heeft om voor te vullen. */
export interface WerkbonVerstuurVoorbeeldDTO {
  werkbonNummer: string
  klantNaam: string
  datum: string
  /** Voorgevulde naar-adressen volgens de cascade (zonder expliciete keuze). */
  defaultTo: string[]
  /** Voorgevulde cc volgens de standaard-cc van de klant. */
  defaultCc: string[]
  /** Contactpersonen van de klant mét e-mailadres (voor de picker). */
  contacten: WerkbonVerstuurContactDTO[]
  /** Leesbare context "Vaste ontvanger: Freek Twilt - hoofd TD" of `null`. */
  vasteOntvangerLabel: string | null
  /** E-mailadres van de ingelogde gebruiker (voor "Mijzelf in cc"). */
  eigenEmail: string | null
  /** De effectieve mailtekst (per-klant/standaard, of eenmalig indien meegestuurd). */
  effectief: WerkbonMailEffectiefDTO
  /** De ruwe (niet-gerenderde) effectieve tekst, voor de bewerkbare velden. */
  bewerkbaar: WerkbonMailTekstInput
}

/** Read-DTO van de per-klant werkbon-mailtekst-instellingen (relatie-detail). */
export interface ClientWerkbonMailInstellingenDTO {
  /** Vaste ontvanger als contactpersoon-verwijzing (of `null`). */
  werkbonOntvangerContactId: string | null
  /** Vaste ontvanger als vrij adres (of `null`). */
  werkbonOntvangerEmail: string | null
  /** Standaard-cc-adressen. */
  werkbonStandaardCc: string[]
  /** Heeft de klant een eigen (niet-verwijderde) mailtekst? */
  heeftEigenTekst: boolean
  /** De eigen tekst (alleen gevuld bij `heeftEigenTekst`). */
  eigenSubject: string | null
  eigenBodyHtml: string | null
  /** De standaard-tekst (actieve sjabloonversie), als fallback-referentie. */
  standaardSubject: string
  standaardBodyHtml: string
  /** Contactpersonen mét e-mailadres (voor de vaste-ontvanger-picker). */
  contacten: WerkbonVerstuurContactDTO[]
}

/** Body van de override-preview (live preview in de relatie-tab editor). */
export const WerkbonMailVoorbeeldSchema = z.object({
  subject: z.string().max(500),
  bodyHtml: z.string(),
})
export type WerkbonMailVoorbeeldInput = z.infer<typeof WerkbonMailVoorbeeldSchema>
